import {
  SafeTypedCell,
  SafeTypedTableRow,
  SortDirection,
  TableCellType,
  TableHeader,
  TableRow,
} from "./types"

export const DEFAULT_CELL_NODE = "--"
export const DEFAULT_CELL_NUMBER = -999
export const DEFAULT_CELL_DATE = new Date(0).toISOString()
export const DEFAULT_CELL_STRING = ""
export const DATA_FOCUSABLE_CELL = "data-table-focusable-cell"

export function getSafeTypedData(
  rows: TableRow[],
  headers: TableHeader[],
  defaultCellNode?: string,
): SafeTypedTableRow[] {
  return rows.map((row) => ({
    ...row,
    data: {
      ...row.data,
      cells: row.data.cells.map((cell, index) => {
        if (
          cell.value === null ||
          cell.value === undefined ||
          cell.value === ""
        ) {
          let safeTypedValue: string | number
          if (headers[index].type === TableCellType.NUMBER) {
            safeTypedValue = DEFAULT_CELL_NUMBER
          } else if (headers[index].type === TableCellType.ISO_DATE_STRING) {
            safeTypedValue = DEFAULT_CELL_DATE
          } else {
            safeTypedValue = DEFAULT_CELL_STRING
          }
          return {
            id: cell.id,
            value: safeTypedValue,
            node: cell.node ?? defaultCellNode ?? DEFAULT_CELL_NODE,
            sortValue: cell.sortValue ?? undefined,
          }
        }
        return cell as SafeTypedCell
      }),
    },
  }))
}

export function getSortedRows(
  data: SafeTypedTableRow[],
  sortState: { col: number | null; dir: SortDirection | null },
  headers: TableHeader[],
): SafeTypedTableRow[] {
  if (sortState.col === null || sortState.dir === null) return data

  const colIdx = sortState.col
  return [...data].sort((a, b) => {
    const cellType = headers[colIdx].type
    const aCell = a.data.cells[colIdx]
    const bCell = b.data.cells[colIdx]
    // `sortValue` lets a column sort on something other than its displayed value
    // (e.g. sort a full-name column by last name). An empty string is treated
    // the same as a missing override so a row without one (e.g. no last name)
    // falls back to its display value instead of sorting as blank.
    const resolveSortValue = (cell: SafeTypedCell) =>
      cell.sortValue === null ||
      cell.sortValue === undefined ||
      cell.sortValue === ""
        ? cell.value
        : cell.sortValue
    const aVal = resolveSortValue(aCell)
    const bVal = resolveSortValue(bCell)

    const placeholders = [
      DEFAULT_CELL_NODE,
      DEFAULT_CELL_NUMBER,
      DEFAULT_CELL_DATE,
      DEFAULT_CELL_STRING,
    ]
    const isAPlaceholder = placeholders.includes(aCell.value)
    const isBPlaceholder = placeholders.includes(bCell.value)
    if (isAPlaceholder && !isBPlaceholder) return 1
    if (!isAPlaceholder && isBPlaceholder) return -1
    if (isAPlaceholder && isBPlaceholder) return 0

    if (cellType === TableCellType.NUMBER) {
      const aNum = aVal as number
      const bNum = bVal as number
      return sortState.dir === SortDirection.ASC ? aNum - bNum : bNum - aNum
    }
    if (cellType === TableCellType.ISO_DATE_STRING) {
      const aDate = new Date(aVal as string).getTime()
      const bDate = new Date(bVal as string).getTime()
      return sortState.dir === SortDirection.ASC ? aDate - bDate : bDate - aDate
    }
    return sortState.dir === SortDirection.ASC
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal))
  })
}
